import { useEffect } from 'react';
import { warmAllSectionComponents } from '../canvas/sectionRegistry';
import { bt, btp } from '../i18n';
import { BuilderCanvas } from '../canvas/BuilderCanvas';
import { useRevealSelection } from '../canvas/useRevealSelection';
import { useRevealRailRow } from './useRevealRailRow';
import { useBuilderStore } from '../state/builderStore';
import { sectionDisplayName } from './sectionVariantLabel';
import { useChromeStore, type SidebarTab } from '../state/chromeStore';
import { useBuilderBreakpoint } from '../hooks/useMediaQuery';
import { isReadOnlyView } from '../boot/view-handoff';
import { installAutosaveTriggers } from '../state/autosave';
import { installThemeSaveTriggers } from '../state/saveTheme';
import { installThemeWorkspaceSaveTriggers } from '../state/saveThemeWorkspace';
import { dashboardLoginUrl, deriveDashboardUrl } from '../boot/config';
import { builderDir } from '../i18n/locale';
import { useChromeThemeVars } from '../theme/useChromeThemeVars';
import { ToastHost } from '../ui/ToastHost';
import { LiveRegion } from '../ui/LiveRegion';
import { BottomSheet } from './BottomSheet';
import { ConflictDialog } from './ConflictDialog';
import { GenerationPanel } from './GenerationPanel';
import { GenerationTakeover } from './GenerationTakeover';
import { useGenerationStore } from '../state/generation/generationStore';
import { Inspector } from './Inspector';
import { SectionRail } from './SectionRail';
import { Topbar } from './Topbar';
import { useBuilderShortcuts } from './useBuilderShortcuts';
import './shell.css';

/** Human label for the active sidebar tab (sheet title on mobile when nothing is selected). */
const TAB_TITLES: Record<SidebarTab, string> = {
  sections: bt('chrome.tabs.sections', 'Sections'),
  theme: bt('chrome.tabs.theme', 'Theme settings'),
};

/**
 * The builder shell (M3.1) — the editor (left column: sections rail + inspector · centre canvas)
 * + topbar, fed by the Zustand store loaded from `GET /api/builder/page`. Layout + rail/canvas/inspector
 * seams match the validated Angular mockup geometry (build-plan §7.1); the canvas renders the REAL draft
 * through the granular `BuilderCanvas`/`MemoSection` path (the same component tree the storefront paints).
 *
 * RESPONSIVE CHROME — a faithful clone of Shopify's theme editor, three modes (see `useBuilderBreakpoint`
 * + the shell.css `[data-mode]` rules). The engine, reducers, dnd, undo/redo, and renderer are untouched;
 * only WHERE the existing rail/inspector panels render changes:
 *   - DESKTOP (≥1024px): THREE columns — the section tree (LEFT), the preview canvas (CENTRE, filling the
 *     remaining width), and the inspector as its own RIGHT-HAND column when a section is selected (settings
 *     on the right, founder 2026-07-08). The inspector is a column ONLY on desktop; with nothing selected
 *     there is no empty right column and the preview fills that width.
 *   - TABLET  (768–1023px): the left rail stays a NORMAL sidebar; the inspector becomes a BOTTOM SHEET
 *     over the preview when a section is selected.
 *   - MOBILE  (<768px): a full-bleed preview + a SINGLE bottom sheet showing the active panel (the
 *     sections tree / inspector / theme settings), driven by the existing tab + selection.
 */
export function BuilderShell() {
  const loadState = useBuilderStore((s) => s.loadState);
  useWarmSectionLibrary(loadState);
  const context = useBuilderStore((s) => s.renderContext);
  const meta = useBuilderStore((s) => s.meta);
  const load = useBuilderStore((s) => s.load);
  const device = useBuilderStore((s) => s.device);
  const previewMode = useBuilderStore((s) => s.previewMode);
  const selectedSectionId = useBuilderStore((s) => s.selectedSectionId);
  const selectSection = useBuilderStore((s) => s.selectSection);
  /**
   * The Countdown APP's surface is the editor's THIRD selectable thing (0041 V7). The panel used to be
   * gated on a SECTION id alone, so selecting the app's bar opened nothing at all — the click worked,
   * the store knew, and the inspector was simply never mounted (measured 29/09/2026).
   */
  const selectedCountdown = useBuilderStore((s) => s.selectedCountdown);
  const selectCountdownSurface = useBuilderStore((s) => s.selectCountdownSurface);
  // NB: these two live HERE, with the other store reads, and not beside `inspectorSelected` below —
  // the component early-returns on `loadState` a few lines further down, so a hook declared after it
  // runs on some renders and not others. That is React error #310, and it blanked the whole editor
  // (measured 29/09/2026; the harness caught it before she did).
  const selectedSection = useBuilderStore((s) =>
    s.selectedSectionId ? s.sectionsById[s.selectedSectionId] : undefined,
  );
  const sidebarTab = useChromeStore((s) => s.sidebarTab);
  const sidebarCollapsed = useChromeStore((s) => s.sidebarCollapsed);
  const setSidebarCollapsed = useChromeStore((s) => s.setSidebarCollapsed);

  // The responsive MODE (Shopify theme-editor parity). The CSS in shell.css gates the layout off the
  // `data-mode` attribute below; this hook is the single source of truth shared with the breakpoints.
  const mode = useBuilderBreakpoint();
  const isTablet = mode === 'tablet';
  const isMobile = mode === 'mobile';

  // Builder-scoped shortcuts (Cmd/Ctrl+Z undo, +Shift+Z redo, Cmd/Ctrl+S save) — focus-aware
  // (build-plan §5.3, §7.6). Hook is called unconditionally before the early returns (rules of hooks).
  useBuilderShortcuts();

  // Feature 0059 takeover: while the creation run owns the screen, the editor chrome disappears
  // (`.bld--genrun`). UNCONDITIONALLY BEFORE the early returns — first placed after them, which is
  // the exact "Rendered more hooks than during the previous render" crash (rules of hooks): the boot
  // render returned early without it, the ready render called it, React counted a new hook.
  const genTakeoverRunning = useGenerationStore(
    (s) =>
      s.mode === 'takeover' &&
      (s.status === 'running' || s.status === 'done' || s.status === 'unavailable'),
  );
  // Inside the dashboard's creation modal after «Explorer ma boutique»: component PREVIEW only —
  // the editor chrome never renders in the iframe (founder 2026-08-21); editing top-navigates out.
  const embeddedPreview = useGenerationStore((s) => s.embeddedPreview);

  // The theme palette as CSS vars on the document root, so the CHROME (inspector swatches, token grid)
  // can paint `var(--aqx-theme-*)` — the canvas' own projection only covers the preview.
  useChromeThemeVars();

  // Scroll the canvas to the selected component whenever the left tree (or an insert/duplicate) requests
  // a reveal — Shopify parity: picking a section/block in the rail jumps the preview to it even from the
  // footer. Keyed on the store's `revealNonce` so canvas CLICKS never reposition the preview. Called
  // here (rendered once, before the early returns) rather than in the concurrently-evolving canvas file.
  useRevealSelection();

  // …and the MIRROR: scroll the LEFT TREE to the selected row. A click in the canvas selects the row and
  // opens its inspector, but on a long tree that row was often off-screen — you could see WHAT you edited
  // on the right without seeing WHICH component it was on the left (founder 2026-08-01). Keyed on the
  // selection itself: a row that is already visible scrolls nothing, so tree clicks stay still.
  useRevealRailRow();

  // Install the force-flush triggers (visibilitychange→hidden / blur → keepalive last-save; online →
  // resume; beforeunload native guard) once for the editor session (build-plan §5.4). Idempotent so
  // StrictMode's double-mount doesn't double-register.
  // Superadmin consultation: no save channel exists, so none of the flush triggers get installed —
  // the gates inside the save modules are the guarantee, this is the quiet.
  const readOnlyView = isReadOnlyView();
  useEffect(() => (readOnlyView ? undefined : installAutosaveTriggers()), [readOnlyView]);
  // Feature 0019 — the DEDICATED theme-save keepalive/online triggers (its own versionless channel,
  // decoupled from the page autosave). Idempotent, teardown on unmount.
  useEffect(() => (readOnlyView ? undefined : installThemeSaveTriggers()), [readOnlyView]);
  // Feature 0034 — the same keepalive/online guarantee for the staged theme of an UNPUBLISHED workspace.
  // Its own channel: it writes the workspace sub-key, never the live row, so unlike the theme keepalive
  // above it MUST fire on tab-hide (these are the edits that used to be lost with localStorage).
  useEffect(() => (readOnlyView ? undefined : installThemeWorkspaceSaveTriggers()), [readOnlyView]);

  const pageName = meta.meta?.title ?? bt('chrome.tpl.home', 'Home page');
  // The builder CHROME follows the direction the SELLER reads in (founder, 02/09: « même le
  // builder doit être en arabe si on est en arabe »). The canvas PREVIEW keeps its own direction —
  // `renderContext.dir` inside BuilderCanvas (seeded from the STORE locale, flippable via the
  // topbar's "Preview RTL" toggle → `togglePreviewDir`) — and `dir` on its subtree overrides this.
  //
  // These are two different questions, settled ten weeks apart. 2026-07-04: should the editor flip
  // because the STORE is Arabic? No — a French-reading seller editing an Arabic shop keeps her
  // editor as she reads, only the page flips. 2026-09-02: should it flip because the SELLER reads
  // Arabic? Yes — the chrome is her admin surface, in her language and her direction.
  const dir = builderDir();

  if (loadState === 'session-expired') {
    // The boot gate normally catches this; if a token expires mid-session and refresh fails, bounce.
    window.location.assign(dashboardLoginUrl());
    return <BootMessage text={bt('chrome.boot.redirect', 'Redirecting to sign in…')} />;
  }

  if (loadState === 'no-store') {
    return (
      <FullScreenState
        title={bt('chrome.boot.no_store', 'Create a store first')}
        body={bt(
          'chrome.boot.no_store_body',
          'You need an active store before you can customize its pages.',
        )}
        actionLabel={bt('chrome.boot.go_dashboard', 'Go to dashboard')}
        onAction={() => window.location.assign(deriveDashboardUrl())}
      />
    );
  }

  if (loadState === 'error') {
    return (
      <FullScreenState
        title={bt('chrome.boot.load_failed', 'Couldn’t load this page')}
        body={bt(
          'chrome.boot.load_failed_body',
          'Something went wrong loading your page. Please try again.',
        )}
        actionLabel={bt('chrome.common.retry', 'Retry')}
        onAction={() => void load().catch(() => undefined)}
      />
    );
  }

  // COLD BOOT only (no page loaded yet) shows the full-screen boot. A RE-navigation (switching template —
  // context already exists) keeps the whole editor mounted and shows a light canvas overlay instead, so the
  // switch reads as smooth and the topbar/menu never blank out. (feature 0021)
  //
  // CREATION TAKEOVER EXCEPTION (0059, measured 2026-08-20): the applyTheme seeding + first load runs
  // ~30 s against a slow DB, and during ALL of it this early return used to show a bare
  // "Loading your page…" inside the creation modal — the curtain must own the WHOLE wait, from the
  // first paint. The takeover renders its own full-screen stage, so returning it alone is complete.
  if (loadState === 'idle' || !context) {
    if (genTakeoverRunning) return <GenerationTakeover />;
    return <BootMessage text={bt('chrome.boot.loading', 'Loading your page…')} />;
  }
  const navigating = loadState === 'loading';

  // ── Responsive panel placement ──────────────────────────────────────────────
  // DESKTOP: the rail (left) + inspector (right) render as the classic side columns around the preview.
  // TABLET : the rail renders as a normal sidebar column; the inspector moves into a bottom sheet.
  // MOBILE : neither renders as a column; a single bottom sheet shows the active panel.
  const inspectorSelected =
    !previewMode && ((!!selectedSectionId && !!selectedSection) || !!selectedCountdown);

  // The left rail renders as a column on DESKTOP and TABLET. On DESKTOP it stays collapsible exactly as
  // before (the icon-rail tab toggles `sidebarCollapsed` — preserved pixel-identical). On TABLET it is
  // ALWAYS visible regardless of `sidebarCollapsed` (the founder's explicit ask). On mobile it lives
  // inside the sheet instead.
  const railAsColumn = !previewMode && (isTablet || (mode === 'desktop' && !sidebarCollapsed));

  // On DESKTOP the inspector is its own RIGHT-HAND column beside the preview (settings on the right —
  // founder 2026-07-08). It renders only when a section is actually selected (nothing selected → no empty
  // right column, the preview fills that width). On TABLET/MOBILE the inspector is NOT a column — its
  // content goes to the bottom sheet instead (see below).
  const inspectorAsRightColumn = !previewMode && mode === 'desktop' && inspectorSelected;

  // What the bottom sheet shows + its title.
  //   - Tablet: the sheet appears ONLY when a section is selected → the inspector.
  //   - Mobile: the sheet is ALWAYS present (outside preview) → inspector if selected, else the active
  //     sidebar tab's panel (sections tree / theme list), all rendered by <SectionRail/>.
  const selectedName = selectedSection ? sectionDisplayName(selectedSection) : null;

  let sheet: {
    content: React.ReactNode;
    title: React.ReactNode;
    label: string;
    onClose: () => void;
  } | null = null;
  if (!previewMode) {
    const inspectorSheet = {
      content: <Inspector />,
      title: selectedCountdown
        ? bt('chrome.insp.countdown_title', 'Countdown')
        : (selectedName ?? bt('secname.fallback', 'Section')),
      label: selectedCountdown
        ? bt('chrome.insp.countdown_aria', 'Countdown design')
        : bt('chrome.sheet.section_settings', 'Section settings'),
      onClose: () => (selectedCountdown ? selectCountdownSurface(null) : selectSection(null)),
    };
    if (isTablet && inspectorSelected) {
      sheet = inspectorSheet;
    } else if (isMobile) {
      if (inspectorSelected) {
        sheet = inspectorSheet;
      } else {
        sheet = {
          content: <SectionRail />,
          title: TAB_TITLES[sidebarTab],
          label: TAB_TITLES[sidebarTab],
          // Collapsing the rail on mobile hides the sheet (the seller is back to the bare preview).
          onClose: () => setSidebarCollapsed(true),
        };
      }
    }
  }

  // On mobile the tab panel lives inside the sheet, so the sheet can be DISMISSED: `sidebarCollapsed` is
  // reused as the "sheet dismissed" flag there. A selected section always re-shows the inspector sheet.
  const showSheet = !!sheet && !(isMobile && !inspectorSelected && sidebarCollapsed);

  return (
    <div
      className={genTakeoverRunning ? 'bld bld--genrun' : 'bld'}
      dir={dir}
      data-device={device}
      data-preview={previewMode || undefined}
      data-mode={mode}
    >
      <a className="bld-skip-link" href="#bld-canvas">
        {bt('chrome.top.skip', 'Skip to canvas')}
      </a>
      <h1 className="bld-sr-only">
        {btp('chrome.top.h1', 'Theme builder — {name}', { name: pageName })}
      </h1>

      {embeddedPreview ? (
        <div className="bld-embedbar">
          <span className="bld-embedbar__label">
            {bt('chrome.embed.preview', 'Previewing your store')}
          </span>
          <button
            type="button"
            className="bld-embedbar__open"
            onClick={() => useGenerationStore.getState().openEditor()}
          >
            Ouvrir le builder
          </button>
        </div>
      ) : (
        <Topbar />
      )}

      {readOnlyView && (
        <div className="bld-viewbanner" role="status">
          {bt('chrome.view.banner', 'Read-only consultation - changes are not saved.')}
        </div>
      )}

      <div className="bld-body">
        {/* LEFT column — the section tree (desktop + tablet, a normal sidebar). On mobile the tree lives
            inside the bottom sheet instead, so it renders no column here. */}
        {railAsColumn && <SectionRail />}

        <main
          id="bld-canvas"
          className="bld-center"
          aria-label={
            previewMode
              ? bt('chrome.canvas.aria_live', 'Live page preview')
              : bt('chrome.canvas.aria', 'Page preview')
          }
          tabIndex={-1}
        >
          <div className="bld-canvas">
            <BuilderCanvas />
          </div>
          {navigating && (
            <div className="bld-canvas__nav" role="status" aria-live="polite">
              <div className="bld-boot__spinner" aria-hidden="true" />
              <span className="bld-sr-only">{bt('chrome.boot.loading', 'Loading your page…')}</span>
            </div>
          )}
        </main>

        {/* RIGHT column — the inspector (desktop only, when a section is selected). On tablet/mobile its
            content renders inside the bottom sheet below instead of as a column. */}
        {inspectorAsRightColumn && <Inspector />}

        {/* Bottom sheet (tablet + mobile) — overlays the preview area, full width, draggable to resize.
            Renders the existing rail/inspector panels UNCHANGED inside its scrolling body. */}
        {showSheet && sheet && (
          <BottomSheet
            title={sheet.title}
            ariaLabel={sheet.label}
            closeLabel={bt('chrome.sheet.close', 'Close panel')}
            onClose={sheet.onClose}
          >
            {sheet.content}
          </BottomSheet>
        )}
      </div>

      <ConflictDialog />
      <GenerationPanel />
      <GenerationTakeover />
      <ToastHost />
      <LiveRegion />
    </div>
  );
}

function BootMessage({ text }: { text: string }) {
  return (
    <div className="bld-boot" role="status" aria-live="polite">
      <div className="bld-boot__panel">
        <div className="bld-boot__spinner" aria-hidden="true" />
        <p className="bld-boot__text">{text}</p>
      </div>
    </div>
  );
}

function FullScreenState({
  title,
  body,
  actionLabel,
  onAction,
}: {
  title: string;
  body: string;
  actionLabel: string;
  onAction: () => void;
}) {
  return (
    <div className="bld-boot" role="alert">
      <div className="bld-boot__panel">
        <h1 className="bld-boot__title">{title}</h1>
        <p className="bld-boot__text">{body}</p>
        <button type="button" className="bld-btn bld-btn--primary" onClick={onAction}>
          {actionLabel}
        </button>
      </div>
    </div>
  );
}

/**
 * Stream the REST of the section library in, once the seller's page is on screen.
 *
 * The editor fetches a page's own sections before it paints and no others — that split is what took the
 * cold boot's entry chunk from 802 KB to 372 KB over the wire (preprod, 2026-09-16). The other fifty-odd
 * are not needed to open the page, but they ARE needed the moment the seller opens "Add section", whose
 * preview pane renders a live thumbnail of whatever they hover. Leaving those to arrive on hover trades one
 * long wait at boot for a short one on every section they consider — a worse editor, and the same bytes.
 *
 * So they arrive here: after the first paint, in an idle callback, never on the boot path. By the time a
 * picker is open they are in hand, and every later page load is instant whatever sections it holds.
 *
 * Runs ONCE per session — `loadSectionComponents` skips what it already has, but the effect guards anyway
 * so a route change does not re-schedule an idle callback for nothing. `requestIdleCallback` is absent on
 * Safari before 17; the timeout fallback there is deliberately generous, because the whole point is to stay
 * out of the way of a canvas that is still settling.
 */
function useWarmSectionLibrary(loadState: string): void {
  useEffect(() => {
    if (loadState !== 'ready') return;
    const warm = () => void warmAllSectionComponents();
    const idle = (globalThis as { requestIdleCallback?: (cb: () => void) => number })
      .requestIdleCallback;
    if (idle) {
      const handle = idle(warm);
      return () => {
        (globalThis as { cancelIdleCallback?: (h: number) => void }).cancelIdleCallback?.(handle);
      };
    }
    const t = setTimeout(warm, 2_000);
    return () => clearTimeout(t);
  }, [loadState]);
}
